/**
 * 主题色工具模块
 *
 * 提供"在登录后从服务器同步主题色"的入口。
 * 路由守卫处只需调用 syncThemeFromServer() 即可触发同步。
 */
import { getThemePreference } from '@/api/modules/theme';
import Cookies from 'js-cookie';

const STORAGE_KEY = 'app_theme_primary_color';
const COOKIE_KEY = 'app_theme_primary_color';
const DEFAULT_COLOR = '#304e72';

/**
 * 计算颜色的相对亮度（WCAG 2.x），用于判断主题色是浅色还是深色
 * 浅色背景需用深色文字，深色背景需用浅色文字，以保证对比度
 * @param {String} hex HEX 颜色值，如 '#A1CCF4'
 * @returns {Number} 亮度值 0~1，>0.5 视为浅色
 */
function relativeLuminance(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return 0;
    const num = parseInt(m[1], 16);
    const rsrgb = [(num >> 16) & 255, (num >> 8) & 255, num & 255];
    const linear = rsrgb.map(c => {
        const x = c / 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

/**
 * 应用主题色到 :root（可在 Vue 加载前调用，避免闪烁）
 * 同时根据亮度设置 data-theme 属性（light/dark），供需要随主题切换前景色的组件使用
 * @param {String} color 主题色 HEX 值
 */
export function applyThemeColor(color) {
    if (!color) return;
    document.documentElement.style.setProperty('--primary-color', color);
    const isLight = relativeLuminance(color) > 0.5;
    document.documentElement.setAttribute('data-theme', isLight ? 'light' : 'dark');
}

/**
 * 从 localStorage 或 Cookie 恢复主题色（在 main.js 最早调用，避免 FOUC 闪烁）
 * localStorage 被清除时（如用户手动清除），从 Cookie 兜底恢复
 */
export function restoreThemeFromStorage() {
    let saved = window.localStorage.getItem(STORAGE_KEY);
    // localStorage 没有时，从 Cookie 兜底（Cookie 不会因清除 localStorage 而丢失）
    if (!saved) {
        saved = Cookies.get(COOKIE_KEY);
        // 如果从 Cookie 恢复了，同步回写 localStorage
        if (saved) {
            window.localStorage.setItem(STORAGE_KEY, saved);
        }
    }
    applyThemeColor(saved || DEFAULT_COLOR);
}

/**
 * 从服务器同步主题色（登录后由路由守卫调用）
 * 直接调用 API 获取，不依赖 ThemeSwitcher 组件是否挂载
 */
export async function syncThemeFromServer() {
    try {
        const res = await getThemePreference();
        if (res && res.state && res.state.code === 200 && res.result && res.result.themeColor) {
            const color = res.result.themeColor;
            applyThemeColor(color);
            window.localStorage.setItem(STORAGE_KEY, color);
            // 同时写入 Cookie，作为 localStorage 被清除时的兜底
            Cookies.set(COOKIE_KEY, color, { expires: 365 });
            // 同步给 ThemeSwitcher 组件（如果已挂载）
            if (window.__themeSwitcher) {
                window.__themeSwitcher.currentColor = color;
            }
        }
    } catch (e) {
        // 静默失败，使用本地缓存
        console.warn('[theme] 同步服务器主题色失败：', e);
    }
}

/**
 * 读取当前主题色（用于 JS 渲染场景，如 Canvas/ECharts）
 * 从 CSS 变量 --primary-color 读取，读取失败时返回默认值 #304e72
 * @returns {String} HEX 颜色值
 */
export function getPrimaryColor() {
    try {
        const value = getComputedStyle(document.documentElement)
            .getPropertyValue('--primary-color')
            .trim();
        return value || DEFAULT_COLOR;
    } catch (e) {
        return DEFAULT_COLOR;
    }
}

export { STORAGE_KEY, COOKIE_KEY, DEFAULT_COLOR };
